import type { CanonicalFieldDef } from '../domain/canonicalFieldDef';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function splitPath(path: string): string[] {
  return path.split('.').map(segment => segment.trim()).filter(Boolean);
}

function getAtPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = splitPath(path);
  if (parts.length === 0) return undefined;
  let cursor: unknown = obj;
  for (const part of parts) {
    if (!isRecord(cursor)) return undefined;
    cursor = cursor[part];
  }
  return cursor;
}

function setAtPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = splitPath(path);
  if (parts.length === 0) return;
  let cursor: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    const isLast = i === parts.length - 1;
    if (isLast) {
      cursor[part] = value;
      return;
    }
    const next = cursor[part];
    if (!isRecord(next)) {
      cursor[part] = {};
    }
    cursor = cursor[part] as Record<string, unknown>;
  }
}

function deleteAtPath(obj: Record<string, unknown>, path: string): void {
  const parts = splitPath(path);
  if (parts.length === 0) return;
  let cursor: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const part = parts[i];
    const next = cursor[part];
    if (!isRecord(next)) return;
    cursor = next;
  }
  delete cursor[parts[parts.length - 1]];
}

function pruneEmptyParents(obj: Record<string, unknown>, path: string): void {
  const parts = splitPath(path);
  if (parts.length <= 1) return;
  for (let depth = parts.length - 1; depth > 0; depth -= 1) {
    const parentPath = parts.slice(0, depth).join('.');
    const parentValue = getAtPath(obj, parentPath);
    if (isRecord(parentValue) && Object.keys(parentValue).length === 0) {
      deleteAtPath(obj, parentPath);
    } else {
      break;
    }
  }
}

/** Placeholder when adding a new leaf path — aligned with admin-api pathsToExampleJson. */
export function defaultLeafPlaceholder(path: string): unknown {
  const last = path.split('.').pop() ?? path;
  if (last === 'messages') {
    return [{ role: 'user', content: 'string' }];
  }
  if (last === 'securityResponses' || last === 'documentImages') {
    return {};
  }
  if (last === 'mfaRequired' || last === 'useWebSearch') {
    return false;
  }
  if (last === 'usage' || last === 'accountsDetail' || last === 'raw' || last === 'rawResponse') {
    return {};
  }
  return 'string';
}

function walkExampleJson(obj: unknown, prefix = ''): string[] {
  if (Array.isArray(obj)) {
    return prefix ? [prefix] : [];
  }
  if (!isRecord(obj)) {
    return prefix ? [prefix] : [];
  }

  const paths: string[] = [];
  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('_')) continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (isRecord(value)) {
      const nested = walkExampleJson(value, path);
      paths.push(...(nested.length > 0 ? nested : [path]));
    } else if (Array.isArray(value)) {
      paths.push(path);
    } else {
      paths.push(path);
    }
  }
  return paths;
}

/** Extract dot paths for scalar leaves and empty-object leaves from example JSON. */
export function leafPathsFromExampleJson(obj: Record<string, unknown>): string[] {
  return [...new Set(walkExampleJson(obj))].sort();
}

function deepCloneExampleJson(obj: Record<string, unknown>): Record<string, unknown> {
  return JSON.parse(JSON.stringify(obj)) as Record<string, unknown>;
}

/** Fields → JSON: add missing paths, remove orphans, preserve existing leaf values. */
export function mergeExampleJsonFromPaths(
  paths: string[],
  existing: Record<string, unknown>,
): Record<string, unknown> {
  const normalized = [...new Set(paths.map(path => path.trim()).filter(Boolean))];
  const result = deepCloneExampleJson(existing);

  for (const path of normalized) {
    if (getAtPath(result, path) === undefined) {
      setAtPath(result, path, defaultLeafPlaceholder(path));
    }
  }

  const existingPaths = leafPathsFromExampleJson(result);
  const pathSet = new Set(normalized);
  for (const path of existingPaths) {
    if (!pathSet.has(path)) {
      deleteAtPath(result, path);
      pruneEmptyParents(result, path);
    }
  }

  return result;
}

/** JSON → fields: keep metadata for unchanged paths; new paths get defaults. */
export function mergeFieldsFromPaths(
  paths: string[],
  existingFields: CanonicalFieldDef[],
): CanonicalFieldDef[] {
  const normalized = [...new Set(paths.map(path => path.trim()).filter(Boolean))].sort();
  const existingByPath = new Map(
    existingFields.map(field => [field.path.trim(), field] as const),
  );

  return normalized.map(path => {
    const prev = existingByPath.get(path);
    return prev ?? { path, description: '', required: true };
  });
}

export function stringifyExampleJson(obj: Record<string, unknown> | undefined | null): string {
  return JSON.stringify(obj ?? {}, null, 2);
}

export type FormatExampleJsonResult =
  | { ok: true; formatted: string; parsed: Record<string, unknown> }
  | { ok: false; error: string };

export function formatExampleJsonText(text: string): FormatExampleJsonResult {
  try {
    const parsed = JSON.parse(text || '{}') as unknown;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, error: 'Example JSON must be an object' };
    }
    const object = parsed as Record<string, unknown>;
    return { ok: true, formatted: JSON.stringify(object, null, 2), parsed: object };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
  }
}

export function commitExampleJsonToDraftFields(
  text: string,
  existingFields: CanonicalFieldDef[],
): { ok: true; object: Record<string, unknown>; fields: CanonicalFieldDef[] } | { ok: false; error: string } {
  const result = formatExampleJsonText(text);
  if (!result.ok) return result;
  return {
    ok: true,
    object: result.parsed,
    fields: mergeFieldsFromPaths(leafPathsFromExampleJson(result.parsed), existingFields),
  };
}
